/*
 * DRAEN cinematic story.
 *
 * Two jobs: decide which chapter is on screen, and keep the film from running while nobody
 * is watching it. One IntersectionObserver answers the second question and gates the first,
 * so the scroll handler only exists while the section is actually in view.
 */
class DraenCinematic extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.chapters = Array.from(this.querySelectorAll('[data-chapter]'));

    if (!this.chapters.length) return;

    // Motion off: no cross-fade to follow, so both chapters simply stay on the page.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.classList.add('draen-cine--static');
      this.chapters.forEach((chapter) => chapter.classList.add('is-active'));
      if (this.video) this.video.removeAttribute('autoplay');
      return;
    }

    this.show('1');
    this.read = this.read.bind(this);

    // preload="none" keeps the file off the critical path; playback starts once the section
    // is close enough to matter and stops again when it leaves, and the scroll handler lives
    // exactly as long as the film does.
    new IntersectionObserver(([entry]) => this.watch(entry.isIntersecting), {
      rootMargin: '200px 0px',
    }).observe(this);
  }

  watch(visible) {
    if (visible) {
      addEventListener('scroll', this.read, { passive: true });
      this.read();
      if (this.video) {
        const started = this.video.play();
        if (started) started.catch(() => {});
      }
    } else {
      removeEventListener('scroll', this.read);
      if (this.video && !this.video.paused) this.video.pause();
    }
  }

  /* How far the section has travelled through its own pin, 0 to 1. Chapter 02 takes over at
     the halfway mark, while the stage is still stuck to the top of the viewport. */
  read() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      const box = this.getBoundingClientRect();
      const travel = box.height - innerHeight;
      const progress = travel > 0 ? -box.top / travel : 0;
      this.show(progress > 0.5 ? '2' : '1');
    });
  }

  show(index) {
    this.chapters.forEach((chapter) => {
      chapter.classList.toggle('is-active', chapter.dataset.chapter === index);
    });
  }
}

if (!customElements.get('draen-cinematic')) {
  customElements.define('draen-cinematic', DraenCinematic);
}
